import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useGridCopyMenu } from './gridCommon';
import StockSearchInput from './StockSearchInput';
import IntradayTraceSection from './IntradayTraceSection';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ClientSideRowModelModule,
  ModuleRegistry,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';

Highcharts.setOptions({ time: { useUTC: false } });
(window as any).Highcharts = Highcharts;
import { getMainAxios } from '../utils/util';
import { useAuth } from '../context/AuthContext';
import {
  signalLabel, signalColor, signalBg,
  thStyle, tdStyle, tabActiveStyle, paramBoxStyle, paramTitleStyle,
  previewBaseColDefs,
  type PreviewOrder, type SimSignal, type SimVote, type SimResult, type BaseAmtInfo, type QuantStrategyInfo,
} from './quantCommon';

ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule]);

const QuantIntraday: React.FC = () => {
  const copyMenu = useGridCopyMenu();
  const { selectedPfId, canOrder } = useAuth();   // canOrder: 주문 권한 없으면 주문 생성 버튼 숨김
  const axiosInstance = getMainAxios();

  const [strategyList, setStrategyList] = useState<QuantStrategyInfo[]>([]);
  const [intradayConfig, setIntradayConfig] = useState(() => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 10);
    return {
      lookbackDays: 5,
      fromDate: from.toISOString().split('T')[0],
      toDate: to.toISOString().split('T')[0],
      prodId: '',
      enabledStrategies: ['I1', 'I2', 'I3'] as string[],
      ema: { FastPeriod: 5, SlowPeriod: 20 },
      vwap: { Threshold: 0.3 },
      rsi: { Period: 14, Oversold: 30, Overbought: 70 },
      tickIntervalMinutes: 5, // = signalFrequency
      barResolution: 1,
      warmupMinutes: 20,
      sessionStart: '09:00',
      sessionEnd: '15:20',
      dedupMode: 'state-change' as 'every' | 'first' | 'state-change',
    };
  });
  const [traceRunTrigger, setTraceRunTrigger] = useState<number>(0);
  const [intradayResult, setIntradayResult] = useState<SimResult | null>(null);
  const [intradayLoading, setIntradayLoading] = useState(false);
  const [intradayError, setIntradayError] = useState<string | null>(null);
  const [intradayPreviewOrders, setIntradayPreviewOrders] = useState<PreviewOrder[]>([]);
  const [intradayPreviewApplied, setIntradayPreviewApplied] = useState(false);
  const [baseAmtInfo, setBaseAmtInfo] = useState<BaseAmtInfo | null>(null);
  const [savedRunId, setSavedRunId] = useState<number | null>(null);
  const [simRuns, setSimRuns] = useState<any[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<number | ''>('');

  const fetchStrategies = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/api/v1/quant/strategies', { params: { type: 'intraday' } });
      setStrategyList(res.data || []);
    } catch (err) {
      console.error('전략 목록 조회 실패:', err);
    }
  }, []);

  useEffect(() => { fetchStrategies(); }, [fetchStrategies]);

  const fetchSimRuns = useCallback(async () => {
    if (!selectedPfId) return;
    try {
      const res = await axiosInstance.get('/api/v1/quant/sim-runs', {
        params: { pfId: selectedPfId, type: 'intraday', limit: 20 },
      });
      setSimRuns(res.data || []);
    } catch (err) {
      console.error('sim-runs 조회 실패:', err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPfId]);

  useEffect(() => { fetchSimRuns(); }, [fetchSimRuns]);

  const loadSimRun = async (runId: number) => {
    if (!runId) return;
    try {
      const res = await axiosInstance.get(`/api/v1/quant/sim-runs/${runId}`);
      const run = res.data?.run;
      if (!run?.params) {
        alert('이 run에는 재현 데이터가 없습니다.');
        return;
      }
      const parsed = typeof run.params === 'string' ? JSON.parse(run.params) : run.params;
      const snap = parsed?.snapshot;
      if (!snap) {
        alert('이 run은 구 포맷이라 재현할 수 없습니다. (snapshot 없음)');
        return;
      }
      // 파라미터 복원
      setIntradayConfig(prev => ({
        ...prev,
        lookbackDays: parsed.lookbackDays ?? prev.lookbackDays,
        enabledStrategies: parsed.enabledStrategies ?? prev.enabledStrategies,
        prodId: parsed.prodId ?? prev.prodId,
        ema: parsed.ema ?? prev.ema,
        vwap: parsed.vwap ?? prev.vwap,
        rsi: parsed.rsi ?? prev.rsi,
        tickIntervalMinutes: parsed.sliding?.tickIntervalMinutes ?? prev.tickIntervalMinutes,
        warmupMinutes: parsed.sliding?.warmupMinutes ?? prev.warmupMinutes,
        sessionStart: parsed.sliding?.sessionStart ?? prev.sessionStart,
        sessionEnd: parsed.sliding?.sessionEnd ?? prev.sessionEnd,
        dedupMode: parsed.sliding?.dedupMode ?? prev.dedupMode,
      }));
      // 결과 복원
      const restored = {
        fromDate: run.fromDate,
        toDate: run.toDate,
        orderDate: run.orderDate,
        strategies: snap.strategies ?? [],
        signals: snap.signals ?? [],
        votes: snap.votes ?? [],
        tickTimeline: snap.tickTimeline ?? [],
        finalOrders: snap.finalOrders ?? [],
        pnlTrades: snap.pnlTrades ?? [],
        pnlSummary: snap.pnlSummary,
        chartBars: snap.chartBars,
        tickSignalCount: (snap.tickTimeline?.length ?? 0) * 3,
        tickDecisionCount: snap.tickTimeline?.length ?? 0,
        finalOrderCount: snap.finalOrders?.length ?? 0,
      } as any;
      setIntradayResult(restored);
      setSavedRunId(runId);
      setIntradayError(null);
    } catch (err: any) {
      alert('불러오기 실패: ' + (err.response?.data?.error || err.message));
    }
  };

  const buildRequest = (saveOrders: boolean, saveResult: boolean) => ({
    pfId: selectedPfId,
    lookbackDays: intradayConfig.lookbackDays,
    fromDate: intradayConfig.fromDate,
    toDate: intradayConfig.toDate,
    prodId: intradayConfig.prodId || null,
    saveOrders,
    saveResult,
    enabledStrategies: intradayConfig.enabledStrategies,
    ema: intradayConfig.ema,
    vwap: intradayConfig.vwap,
    rsi: intradayConfig.rsi,
    tickIntervalMinutes: intradayConfig.tickIntervalMinutes,
    warmupMinutes: intradayConfig.warmupMinutes,
    sessionStart: intradayConfig.sessionStart,
    sessionEnd: intradayConfig.sessionEnd,
    dedupMode: intradayConfig.dedupMode,
  });

  const runIntradaySimulation = async () => {
    if (!selectedPfId) return;
    setIntradayLoading(true);
    setIntradayError(null);
    setIntradayResult(null);
    setIntradayPreviewOrders([]);
    setIntradayPreviewApplied(false);
    setSavedRunId(null);
    try {
      const res = await axiosInstance.post('/api/v1/quant/intraday-simulate', buildRequest(false, false), { timeout: 60000 });
      setIntradayResult(res.data);
      // 트레이스 섹션도 함께 실행 (종목 지정된 경우만)
      if (intradayConfig.prodId) {
        setTraceRunTrigger(v => v + 1);
      }
    } catch (err: any) {
      setIntradayError(err.response?.data?.error || err.message || '인트라데이 시뮬레이션 실패');
    } finally {
      setIntradayLoading(false);
    }
  };

  const saveResult = async () => {
    if (!selectedPfId || !intradayResult) return;
    setIntradayLoading(true);
    try {
      const res = await axiosInstance.post('/api/v1/quant/intraday-simulate', buildRequest(false, true), { timeout: 60000 });
      const runId = res.data?.runId;
      if (runId) {
        setSavedRunId(runId);
        fetchSimRuns();
        alert(`시뮬레이션 결과 저장 완료 (run #${runId}). 이력 화면에서 조회할 수 있습니다.`);
      } else {
        alert('저장 실패: runId 없음');
      }
    } catch (err: any) {
      alert('저장 실패: ' + (err.response?.data?.error || err.message));
    } finally {
      setIntradayLoading(false);
    }
  };

  const intradayGeneratePreview = async () => {
    if (!selectedPfId || !intradayResult?.orderDate) return;
    if (selectedPfId === 0) { alert('주문은 개별 계좌를 선택해야 합니다.'); return; }
    setIntradayLoading(true);
    setIntradayPreviewOrders([]);
    setIntradayPreviewApplied(false);
    try {
      await axiosInstance.post('/api/v1/quant/intraday-simulate', buildRequest(true, false), { timeout: 60000 });
      const res = await axiosInstance.post('/api/v1/quant/generate-orders', null, {
        params: { pfId: selectedPfId, d: intradayResult.orderDate, orderQuantity: 100, preview: true },
      });
      const orders: PreviewOrder[] = (res.data.orders || []).map((o: PreviewOrder) => ({ ...o, selected: true }));
      setIntradayPreviewOrders(orders);
      if (res.data.baseAmtInfo) setBaseAmtInfo(res.data.baseAmtInfo);
      if (orders.length === 0) alert('생성 가능한 주문이 없습니다.');
    } catch (err: any) {
      setIntradayError(err.response?.data?.error || err.message);
    } finally {
      setIntradayLoading(false);
    }
  };

  const intradayApplyOrders = async () => {
    if (!selectedPfId || !intradayResult?.orderDate) return;
    const selected = intradayPreviewOrders.filter(o => o.selected);
    if (selected.length === 0) { alert('선택된 종목이 없습니다.'); return; }
    if (!confirm(`${intradayResult.orderDate} 선택된 ${selected.length}건을 실제 주문으로 생성하시겠습니까?`)) return;
    setIntradayLoading(true);
    try {
      const prodIds = selected.map(o => o.prodId).join(',');
      const res = await axiosInstance.post('/api/v1/quant/generate-orders', null, {
        params: { pfId: selectedPfId, d: intradayResult.orderDate, orderQuantity: 100, prodIds },
      });
      const { orderCount, deleted, skipped } = res.data;
      setIntradayPreviewApplied(true);
      alert(`기존 ${deleted}건 삭제, ${orderCount}건 주문 생성${skipped > 0 ? `, ${skipped}건 매수불가 스킵` : ''}`);
    } catch (err: any) {
      alert('주문 적용 실패: ' + (err.response?.data?.error || err.message));
    } finally {
      setIntradayLoading(false);
    }
  };

  const toggleIntradayPreviewSelect = (prodId: string) => {
    setIntradayPreviewOrders(prev => prev.map(o => o.prodId === prodId ? { ...o, selected: !o.selected } : o));
  };
  const toggleIntradayPreviewSelectAll = () => {
    const allSelected = intradayPreviewOrders.every(o => o.selected);
    setIntradayPreviewOrders(prev => prev.map(o => ({ ...o, selected: !allSelected })));
  };

  const toggleIntradayStrategy = (key: string) => {
    setIntradayConfig(prev => ({
      ...prev,
      enabledStrategies: prev.enabledStrategies.includes(key)
        ? prev.enabledStrategies.filter(k => k !== key)
        : [...prev.enabledStrategies, key],
    }));
  };

  // 독립 분봉 차트 (시뮬레이션과 무관)
  const [chartBars, setChartBars] = useState<any[]>([]);
  const [chartMinute, setChartMinute] = useState<number>(5);
  const [chartLoading, setChartLoading] = useState(false);

  const fetchChartBars = useCallback(async (minute?: number) => {
    const prod = intradayConfig.prodId;
    if (!prod) return;
    setChartLoading(true);
    try {
      const res = await axiosInstance.get('/api/v1/quant/intraday-bars', {
        params: { prodId: prod, from: intradayConfig.fromDate, to: intradayConfig.toDate, minute: minute ?? chartMinute },
      });
      setChartBars(Array.isArray(res.data) ? res.data : []);
    } catch { setChartBars([]); }
    finally { setChartLoading(false); }
  }, [intradayConfig.prodId, intradayConfig.fromDate, intradayConfig.toDate, chartMinute]);

  const chartOptions = useMemo<Highcharts.Options | null>(() => {
    if (chartBars.length === 0) return null;

    // CurrentPrice.tsx 와 동일한 방식: 실제 timestamp + breaks 로 야간 갭 제거
    const toEpoch = (d: string, t: string) => {
      const y = d.substring(0, 4), mo = d.substring(4, 6), dy = d.substring(6, 8);
      const hh = t.padStart(6, '0').substring(0, 2), mm = t.padStart(6, '0').substring(2, 4), ss = t.padStart(6, '0').substring(4, 6);
      return new Date(`${y}-${mo}-${dy}T${hh}:${mm}:${ss}+09:00`).getTime();
    };

    const candles = chartBars.map((b: any) =>
      [toEpoch(b.d, b.t), Number(b.o), Number(b.h), Number(b.l), Number(b.c)]
    ).sort((a: any, b: any) => a[0] - b[0]);

    const vols = chartBars.map((b: any) =>
      [toEpoch(b.d, b.t), Number(b.v)]
    ).sort((a: any, b: any) => a[0] - b[0]);

    // 야간 갭 자동 감지 → breaks
    const barStepMs = chartMinute * 60 * 1000;
    const timestamps = candles.map((c: any) => c[0]);
    const breaks: Highcharts.XAxisBreaksOptions[] = [];
    if (timestamps.length > 1) {
      const step = Math.max(barStepMs, 60_000);
      const gapThreshold = Math.max(step * 3, 8 * 60 * 1000);
      for (let i = 1; i < timestamps.length; i++) {
        const gap = timestamps[i] - timestamps[i - 1];
        if (gap > gapThreshold) {
          breaks.push({ from: timestamps[i - 1] + step, to: timestamps[i] - step });
        }
      }
    }

    return {
      time: { timezone: 'Asia/Seoul' },
      title: { text: `${intradayConfig.prodId} - ${chartMinute}분봉 차트`, style: { fontSize: '14px' } },
      plotOptions: {
        series: { dataGrouping: { enabled: false } },
        candlestick: { color: '#ef4444', lineColor: '#ef4444', upColor: '#22c55e', upLineColor: '#22c55e' },
      },
      xAxis: {
        type: 'datetime',
        breaks,
        labels: { style: { fontSize: '11px' } },
      },
      yAxis: [{
        labels: { align: 'right', x: -3 },
        title: { text: 'OHLC' },
        height: '60%',
        lineWidth: 1,
        min: candles.length > 0 ? Math.min(...candles.map((c: any) => c[3])) * 0.98 : undefined,
      }, {
        labels: { align: 'right', x: -3 },
        title: { text: 'Volume' },
        top: '65%',
        height: '35%',
        offset: 0,
        lineWidth: 1,
      }],
      series: [{
        type: 'candlestick',
        name: '가격',
        data: candles,
      } as any, {
        type: 'column',
        name: '거래량',
        data: vols,
        yAxis: 1,
      } as any],
      rangeSelector: { enabled: false },
      navigator: { enabled: true },
      scrollbar: { enabled: true },
      credits: { enabled: false },
    };
  }, [chartBars, chartMinute, intradayConfig.prodId]);

  // 종목별 요약 AG-Grid 컬럼: strategies 동적 컬럼 포함
  const voteColumnDefs = useMemo(() => {
    const r: any = intradayResult;
    if (!r) return [];
    const strategies: string[] = r.strategies || [];
    // 종목별로 전략 signal/confidence 를 미리 맵핑
    const cols: any[] = [
      {
        headerName: '종목', minWidth: 110, flex: 0,
        valueGetter: (p: any) => p.data?.prodNm || p.data?.prodId,
        cellStyle: { fontWeight: 600 },
      },
      { headerName: '코드', field: 'prodId', width: 80, cellStyle: { color: '#9ca3af', fontSize: '10px' } },
      {
        headerName: '종가', width: 90, type: 'rightAligned',
        valueGetter: (p: any) => {
          const sig = (r.signals || []).find((s: any) => s.prodId === p.data?.prodId);
          return sig?.lastClose ?? null;
        },
        valueFormatter: (p: any) => p.value != null ? Number(p.value).toLocaleString() : '',
        cellStyle: { fontFamily: 'monospace', textAlign: 'right' },
      },
      ...strategies.map((sName: string) => ({
        headerName: sName, width: 110,
        valueGetter: (p: any) => {
          const sig = (r.signals || []).find((s: any) => s.prodId === p.data?.prodId && s.strategyName === sName);
          if (!sig) return '';
          return `${signalLabel(sig.signal)} ${(sig.confidence * 100).toFixed(0)}%`;
        },
        cellStyle: (p: any) => {
          const sig = (r.signals || []).find((s: any) => s.prodId === p.data?.prodId && s.strategyName === sName);
          if (!sig) return { textAlign: 'center' };
          return { textAlign: 'center', fontWeight: 700, color: signalColor(sig.signal), backgroundColor: signalBg(sig.signal) };
        },
      })),
      { headerName: 'BUY', field: 'buyCount', width: 60, type: 'rightAligned', cellStyle: { color: '#ef4444', fontWeight: 700, textAlign: 'center' } },
      { headerName: 'SELL', field: 'sellCount', width: 60, type: 'rightAligned', cellStyle: { color: '#3b82f6', fontWeight: 700, textAlign: 'center' } },
      { headerName: 'HOLD', field: 'holdCount', width: 60, type: 'rightAligned', cellStyle: { color: '#6b7280', textAlign: 'center' } },
      { headerName: '결정', field: 'decision', width: 70, cellStyle: (p: any) => {
        const d = p.data?.decision;
        const c = d === 'BUY' ? '#dc2626' : d === 'SELL' ? '#2563eb' : '#6b7280';
        return { color: c, fontWeight: 700, textAlign: 'center' };
      } },
      { headerName: 'wei', field: 'wei', width: 60, type: 'rightAligned' },
    ];
    return cols;
  }, [intradayResult]);

  // Tick 타임라인 AG-Grid 컬럼
  const tickColumnDefs = useMemo(() => [
    { headerName: '시각', field: 'tickTime', width: 130, cellStyle: { fontFamily: 'monospace', fontSize: '10px' } },
    { headerName: '종목', width: 110, valueGetter: (p: any) => p.data?.prodNm || p.data?.prodId },
    { headerName: '코드', field: 'prodId', width: 80, cellStyle: { color: '#9ca3af', fontSize: '10px' } },
    { headerName: '결정', field: 'decision', width: 70, cellStyle: (p: any) => {
      const d = p.data?.decision;
      const c = d === 'BUY' ? '#dc2626' : d === 'SELL' ? '#2563eb' : '#6b7280';
      return { color: c, fontWeight: 700, textAlign: 'center' };
    } },
    { headerName: 'B', field: 'buyCount', width: 48, cellStyle: { color: '#ef4444', textAlign: 'center' } },
    { headerName: 'S', field: 'sellCount', width: 48, cellStyle: { color: '#3b82f6', textAlign: 'center' } },
    { headerName: 'H', field: 'holdCount', width: 48, cellStyle: { color: '#9ca3af', textAlign: 'center' } },
    { headerName: '가격', field: 'price', width: 90, type: 'rightAligned',
      valueFormatter: (p: any) => p.value != null ? Number(p.value).toLocaleString() : '',
      cellStyle: { fontFamily: 'monospace', textAlign: 'right' } },
    { headerName: 'wei', field: 'wei', width: 60, type: 'rightAligned' },
    { headerName: '전략', field: 'bestStrategy', width: 130 },
    { headerName: '근거', field: 'reason', flex: 1, minWidth: 180, cellStyle: { fontSize: '10px', color: '#6b7280' } },
  ], []);

  // P&L 트레이드 AG-Grid 컬럼
  const pnlColumnDefs = useMemo(() => [
    { headerName: '종목', width: 110, valueGetter: (p: any) => p.data?.prodNm || p.data?.prodId, cellStyle: { fontWeight: 600 } },
    { headerName: '진입', field: 'entryTime', width: 130, cellStyle: { fontFamily: 'monospace', fontSize: '10px' } },
    { headerName: '진입가', field: 'entryPrice', width: 90, type: 'rightAligned',
      valueFormatter: (p: any) => p.value != null ? Number(p.value).toLocaleString() : '',
      cellStyle: { fontFamily: 'monospace', textAlign: 'right' } },
    { headerName: '청산', field: 'exitTime', width: 130, cellStyle: { fontFamily: 'monospace', fontSize: '10px' } },
    { headerName: '청산가', field: 'exitPrice', width: 90, type: 'rightAligned',
      valueFormatter: (p: any) => p.value != null ? Number(p.value).toLocaleString() : '',
      cellStyle: { fontFamily: 'monospace', textAlign: 'right' } },
    { headerName: '수량', field: 'qty', width: 65, type: 'rightAligned' },
    { headerName: '손익', field: 'pnl', width: 110, type: 'rightAligned',
      valueFormatter: (p: any) => {
        const v = Number(p.value || 0);
        return (v > 0 ? '+' : '') + v.toLocaleString();
      },
      cellStyle: (p: any) => {
        const v = Number(p.value || 0);
        return { textAlign: 'right', fontWeight: 700, color: v > 0 ? '#dc2626' : v < 0 ? '#2563eb' : '#6b7280' };
      },
    },
    { headerName: '수익률', field: 'returnPct', width: 80, type: 'rightAligned',
      valueFormatter: (p: any) => (Number(p.value || 0) * 100).toFixed(2),
      cellStyle: (p: any) => {
        const v = Number(p.value || 0);
        return { textAlign: 'right', color: v > 0 ? '#dc2626' : v < 0 ? '#2563eb' : '#6b7280' };
      },
    },
    { headerName: '사유', field: 'exitReason', width: 110, cellStyle: { fontSize: '10px', color: '#6b7280' } },
    { headerName: '진입전략', field: 'entryStrategy', width: 130, cellStyle: { fontSize: '10px' } },
    { headerName: '청산전략', field: 'exitStrategy', flex: 1, minWidth: 130, cellStyle: { fontSize: '10px' } },
  ], []);

  const defaultColDef = useMemo(() => ({ resizable: true, sortable: true, filter: false, suppressMovable: false }), []);

  const renderParamInput = (label: string, value: number, onChange: (v: number) => void, step?: number) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <label style={{ fontSize: '11px', color: '#374151', whiteSpace: 'nowrap', minWidth: '60px' }}>{label}</label>
      <input type="number" value={value} onChange={e => onChange(parseFloat(e.target.value) || 0)} step={step ?? 1}
        style={{ width: '70px', padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '11px' }} />
    </div>
  );

  return (
    <div style={{ padding: '20px', backgroundColor: '#eff6ff', minHeight: '100vh' }}>
      {intradayLoading && <div style={{ marginBottom: '10px', fontSize: '11px', color: '#9ca3af' }}>로딩 중...</div>}

      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ margin: 0, fontSize: '12px', fontWeight: 600, color: '#1e40af' }}>인트라데이 전략 파라미터 (분봉 기반)</h3>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <select
              value={selectedRunId}
              onChange={e => {
                const v = e.target.value ? Number(e.target.value) : '';
                setSelectedRunId(v);
                if (v) loadSimRun(v);
              }}
              style={{ padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '11px', minWidth: '220px' }}>
              <option value="">최근 run 불러오기...</option>
              {simRuns.map(r => (
                <option key={r.id} value={r.id}>
                  #{r.id} {r.fromDate}~{r.toDate} ({r.symbolCount}종목 / {r.buyCount}B {r.sellCount}S)
                </option>
              ))}
            </select>
            <button onClick={fetchSimRuns}
              style={{ padding: '4px 8px', fontSize: '11px', border: '1px solid #d1d5db', borderRadius: '3px', backgroundColor: 'white', cursor: 'pointer' }}>
              ↻
            </button>
            <button onClick={runIntradaySimulation} disabled={intradayLoading}
              style={{ ...tabActiveStyle, backgroundColor: intradayLoading ? '#9ca3af' : '#3b82f6', cursor: intradayLoading ? 'not-allowed' : 'pointer' }}>
              {intradayLoading ? '분석 중...' : '시뮬레이션 실행'}
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
          {renderParamInput('조회기간(일)', intradayConfig.lookbackDays, v => setIntradayConfig(prev => {
            const to = new Date(prev.toDate);
            const from = new Date(to);
            from.setDate(from.getDate() - v);
            return { ...prev, lookbackDays: v, fromDate: from.toISOString().split('T')[0] };
          }))}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ fontSize: '11px', color: '#374151', fontWeight: 600 }}>시작:</span>
            <input type="date" value={intradayConfig.fromDate}
              onChange={e => setIntradayConfig(prev => ({ ...prev, fromDate: e.target.value }))}
              style={{ padding: '2px 4px', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '11px' }} />
            <span style={{ fontSize: '11px', color: '#374151', fontWeight: 600, marginLeft: '4px' }}>종료:</span>
            <input type="date" value={intradayConfig.toDate}
              onChange={e => setIntradayConfig(prev => ({ ...prev, toDate: e.target.value }))}
              style={{ padding: '2px 4px', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '11px' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ fontSize: '11px', color: '#374151', fontWeight: 600 }}>종목:</span>
            <StockSearchInput
              value={intradayConfig.prodId}
              onChange={(prodId) => setIntradayConfig(prev => ({ ...prev, prodId }))}
              placeholder="종목검색 (빈칸=전체)"
              width={160}
            />
          </div>
          <span style={{ color: '#d1d5db' }}>|</span>
          <span style={{ fontSize: '11px', color: '#374151', fontWeight: 600 }}>활성전략:</span>
          {strategyList.filter(s => s.enabled).map(s => (
            <button key={s.code} onClick={() => toggleIntradayStrategy(s.code)}
              title={s.description ?? ''}
              style={{
                padding: '3px 8px', borderRadius: '3px', fontSize: '11px', cursor: 'pointer', border: 'none',
                backgroundColor: intradayConfig.enabledStrategies.includes(s.code) ? '#3b82f6' : '#e5e7eb',
                color: intradayConfig.enabledStrategies.includes(s.code) ? 'white' : '#6b7280',
              }}>
              {s.name}
            </button>
          ))}
        </div>

        {/* 슬라이딩 윈도우 옵션 */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', paddingTop: '8px', borderTop: '1px dashed #e5e7eb' }}>
          <span style={{ fontSize: '11px', color: '#1e40af', fontWeight: 600 }}>슬라이딩:</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
               title="Bar Resolution — 전략이 OnBar 로 받는 분봉 해상도">
            <label style={{ fontSize: '11px', color: '#374151' }}>Bar</label>
            <select value={intradayConfig.barResolution}
              onChange={e => {
                const v = parseInt(e.target.value);
                setIntradayConfig(prev => ({
                  ...prev,
                  barResolution: v,
                  // Signal 이 Bar 보다 작아지면 맞춰 올림
                  tickIntervalMinutes: prev.tickIntervalMinutes < v ? v : prev.tickIntervalMinutes,
                }));
              }}
              style={{ padding: '2px 4px', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '11px' }}>
              {[1, 3, 5, 10, 15, 30, 60].map(v => <option key={v} value={v}>{v}분</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
               title="Signal Frequency — 전략 Evaluate() 호출 주기 (Bar 이상)">
            <label style={{ fontSize: '11px', color: '#374151' }}>Signal</label>
            <select value={intradayConfig.tickIntervalMinutes}
              onChange={e => setIntradayConfig(prev => ({ ...prev, tickIntervalMinutes: parseInt(e.target.value) }))}
              style={{ padding: '2px 4px', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '11px' }}>
              {[1, 3, 5, 10, 15, 30, 60].filter(v => v >= intradayConfig.barResolution).map(v =>
                <option key={v} value={v}>{v}분</option>
              )}
            </select>
          </div>
          {renderParamInput('Warmup(분)', intradayConfig.warmupMinutes, v =>
            setIntradayConfig(prev => ({ ...prev, warmupMinutes: v })))}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <label style={{ fontSize: '11px', color: '#374151' }}>세션</label>
            <input type="time" value={intradayConfig.sessionStart}
              onChange={e => setIntradayConfig(prev => ({ ...prev, sessionStart: e.target.value }))}
              style={{ padding: '2px 4px', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '11px', width: '80px' }} />
            <span style={{ fontSize: '11px', color: '#374151' }}>~</span>
            <input type="time" value={intradayConfig.sessionEnd}
              onChange={e => setIntradayConfig(prev => ({ ...prev, sessionEnd: e.target.value }))}
              style={{ padding: '2px 4px', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '11px', width: '80px' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <label style={{ fontSize: '11px', color: '#374151' }}>Dedup</label>
            <select value={intradayConfig.dedupMode}
              onChange={e => setIntradayConfig(prev => ({ ...prev, dedupMode: e.target.value as any }))}
              style={{ padding: '2px 4px', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '11px' }}>
              <option value="state-change">state-change (상태전환만)</option>
              <option value="first">first (하루 첫 신호)</option>
              <option value="every">every (모든 tick)</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
          {intradayConfig.enabledStrategies.includes('I1') && (
            <div style={paramBoxStyle}>
              <div style={{ ...paramTitleStyle, color: '#1e40af' }}>EMA Scalping</div>
              {renderParamInput('Fast', intradayConfig.ema.FastPeriod, v => setIntradayConfig(prev => ({ ...prev, ema: { ...prev.ema, FastPeriod: v } })))}
              {renderParamInput('Slow', intradayConfig.ema.SlowPeriod, v => setIntradayConfig(prev => ({ ...prev, ema: { ...prev.ema, SlowPeriod: v } })))}
            </div>
          )}
          {intradayConfig.enabledStrategies.includes('I2') && (
            <div style={paramBoxStyle}>
              <div style={{ ...paramTitleStyle, color: '#1e40af' }}>VWAP</div>
              {renderParamInput('Threshold(%)', intradayConfig.vwap.Threshold, v => setIntradayConfig(prev => ({ ...prev, vwap: { Threshold: v } })), 0.1)}
            </div>
          )}
          {intradayConfig.enabledStrategies.includes('I3') && (
            <div style={paramBoxStyle}>
              <div style={{ ...paramTitleStyle, color: '#1e40af' }}>RSI</div>
              {renderParamInput('Period', intradayConfig.rsi.Period, v => setIntradayConfig(prev => ({ ...prev, rsi: { ...prev.rsi, Period: v } })))}
              {renderParamInput('Oversold', intradayConfig.rsi.Oversold, v => setIntradayConfig(prev => ({ ...prev, rsi: { ...prev.rsi, Oversold: v } })))}
              {renderParamInput('Overbought', intradayConfig.rsi.Overbought, v => setIntradayConfig(prev => ({ ...prev, rsi: { ...prev.rsi, Overbought: v } })))}
            </div>
          )}
        </div>
      </div>

      {intradayResult && (
        <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', overflow: 'hidden', marginBottom: '16px' }}>
          <div style={{ padding: '12px 16px', backgroundColor: '#f3f4f6', borderBottom: '1px solid #e5e7eb' }}>
            <span style={{ fontSize: '11px', color: '#1e40af' }}>
              분석기간: {intradayResult.fromDate} ~ {intradayResult.toDate} | 전략: {intradayResult.strategies.join(', ')}
            </span>
          </div>
          <div className="ag-theme-quartz" style={{ height: Math.min(80 + (intradayResult.votes?.length || 0) * 36, 420), width: '100%', fontSize: '12px' }}>
            <AgGridReact
              {...copyMenu.gridProps}
              rowData={intradayResult.votes as any[]}
              columnDefs={voteColumnDefs}
              defaultColDef={defaultColDef}
              headerHeight={32}
              rowHeight={30}
              overlayNoRowsTemplate="시뮬레이션 결과 없음"
            />
          </div>
          {/* tick 타임라인 (슬라이딩 결과) */}
          {(intradayResult as any).tickTimeline && (intradayResult as any).tickTimeline.length > 0 && (
            <div style={{ padding: '10px 16px', borderTop: '1px solid #e5e7eb', backgroundColor: '#fafafa' }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: '#1e40af', marginBottom: '6px' }}>
                Tick 타임라인 ({(intradayResult as any).tickSignalCount}건 신호 / {(intradayResult as any).tickDecisionCount}건 결정 / {(intradayResult as any).finalOrderCount}건 최종)
              </div>
              <div className="ag-theme-quartz" style={{ height: 300, width: '100%', fontSize: '11px' }}>
                <AgGridReact
                  {...copyMenu.gridProps}
                  rowData={(intradayResult as any).tickTimeline as any[]}
                  columnDefs={tickColumnDefs}
                  defaultColDef={defaultColDef}
                  headerHeight={30}
                  rowHeight={26}
                  pagination={true}
                  paginationPageSize={200}
                  overlayNoRowsTemplate="tick 신호 없음"
                />
              </div>
            </div>
          )}
          <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '11px', color: '#6b7280' }}>
              {intradayResult.votes.filter((v: SimVote) => v.decision !== 'HOLD').length}개 종목 주문 가능
              {intradayResult.orderDate && <span style={{ marginLeft: '6px' }}>→ 주문일: {intradayResult.orderDate}</span>}
              {savedRunId && <span style={{ marginLeft: '8px', color: '#16a34a', fontWeight: 600 }}>저장됨 (run #{savedRunId})</span>}
            </span>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button onClick={saveResult} disabled={intradayLoading || savedRunId != null}
                style={{
                  padding: '6px 16px', borderRadius: '3px', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                  backgroundColor: (intradayLoading || savedRunId != null) ? '#9ca3af' : '#16a34a', color: 'white',
                }}>
                {savedRunId != null ? '저장 완료' : '결과 저장'}
              </button>
              {canOrder && (
              <button onClick={intradayGeneratePreview}
                disabled={intradayLoading || intradayPreviewApplied}
                style={{
                  padding: '6px 16px', borderRadius: '3px', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                  backgroundColor: (intradayLoading || intradayPreviewApplied) ? '#9ca3af' : '#f59e0b', color: 'white',
                }}>
                {intradayLoading ? '처리 중...' : intradayPreviewApplied ? '주문 적용 완료' : '예상주문생성'}
              </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 분봉 차트 (시뮬레이션과 독립) */}
      {intradayConfig.prodId && (
        <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: '#1e40af' }}>{intradayConfig.prodId} 분봉 차트</span>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              {[1, 3, 5, 10, 60].map(m => (
                <button key={m} onClick={() => { setChartMinute(m); fetchChartBars(m); }}
                  style={{
                    padding: '3px 10px', border: 'none', borderRadius: '3px', fontSize: '11px', cursor: 'pointer',
                    backgroundColor: chartMinute === m ? '#3b82f6' : '#e5e7eb',
                    color: chartMinute === m ? 'white' : '#374151', fontWeight: chartMinute === m ? 600 : 400,
                  }}>
                  {m === 60 ? '60분' : `${m}분`}
                </button>
              ))}
              <button onClick={() => fetchChartBars()} disabled={chartLoading}
                style={{ padding: '3px 10px', backgroundColor: chartLoading ? '#9ca3af' : '#3b82f6', color: 'white',
                  border: 'none', borderRadius: '3px', fontSize: '11px', cursor: chartLoading ? 'not-allowed' : 'pointer' }}>
                {chartLoading ? '조회중...' : '조회'}
              </button>
            </div>
          </div>
          {chartOptions ? (
            <HighchartsReact highcharts={Highcharts} constructorType="stockChart" options={chartOptions} />
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af', fontSize: '12px' }}>
              분봉 단위를 선택하고 '조회' 버튼을 클릭하세요.
            </div>
          )}
        </div>
      )}

      {/* P&L 요약 + 트레이드 리스트 */}
      {(intradayResult as any)?.pnlSummary && (intradayResult as any).pnlSummary.totalTrades > 0 && (() => {
        const r: any = intradayResult;
        const s = r.pnlSummary;
        const profit = Number(s.totalPnL) >= 0;
        return (
          <div style={{ backgroundColor: 'white', border: '2px solid #10b981', borderRadius: '8px', marginBottom: '16px', overflow: 'hidden' }}>
            <div style={{ padding: '10px 16px', backgroundColor: '#ecfdf5', borderBottom: '1px solid #d1fae5', display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', fontWeight: 700, color: '#065f46' }}>백테스트 P&L</span>
              <span style={{ fontSize: '11px', color: '#374151' }}>거래 <b>{s.totalTrades}</b></span>
              <span style={{ fontSize: '11px', color: '#374151' }}>승률 <b>{(Number(s.winRate) * 100).toFixed(1)}%</b> ({s.winTrades}승 / {s.lossTrades}패)</span>
              <span style={{ fontSize: '12px', fontWeight: 700, color: profit ? '#dc2626' : '#2563eb' }}>
                총손익 {profit ? '+' : ''}{Number(s.totalPnL).toLocaleString()}원
              </span>
              <span style={{ fontSize: '11px', color: '#374151' }}>평균수익 {Number(s.avgWin).toLocaleString()}</span>
              <span style={{ fontSize: '11px', color: '#374151' }}>평균손실 {Number(s.avgLoss).toLocaleString()}</span>
              <span style={{ fontSize: '11px', color: '#374151' }}>MDD {Number(s.maxDrawdown).toLocaleString()}</span>
              <span style={{ fontSize: '11px', color: '#374151' }}>최고 {Number(s.bestTrade).toLocaleString()}</span>
              <span style={{ fontSize: '11px', color: '#374151' }}>최악 {Number(s.worstTrade).toLocaleString()}</span>
            </div>
            <div className="ag-theme-quartz" style={{ height: Math.min(90 + (r.pnlTrades?.length || 0) * 30, 360), width: '100%', fontSize: '11px' }}>
              <AgGridReact
                {...copyMenu.gridProps}
                rowData={r.pnlTrades as any[]}
                columnDefs={pnlColumnDefs}
                defaultColDef={defaultColDef}
                headerHeight={30}
                rowHeight={26}
                overlayNoRowsTemplate="트레이드 없음"
              />
            </div>
          </div>
        );
      })()}

      {intradayPreviewOrders.length > 0 && (
        <div style={{ marginTop: '16px' }}>
          <div style={{ backgroundColor: '#fffbeb', border: '2px solid #f59e0b', borderBottom: 'none', borderRadius: '8px 8px 0 0', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <span style={{ fontSize: '12px', fontWeight: 600, color: '#92400e' }}>
                예상주문 미리보기 ({intradayPreviewOrders.length}건, 선택 {intradayPreviewOrders.filter(o => o.selected).length}건)
              </span>
              {baseAmtInfo && (
                <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>
                  계산기준({baseAmtInfo.bsDate}): D+2 {baseAmtInfo.n2TotAmt.toLocaleString()}원 → 매수가능 {baseAmtInfo.buyableAmt.toLocaleString()}원
                </div>
              )}
            </div>
            {intradayPreviewApplied ? (
              <span style={{ fontSize: '12px', fontWeight: 600, color: '#22c55e' }}>주문 적용 완료</span>
            ) : (
              <button onClick={intradayApplyOrders}
                disabled={intradayLoading || intradayPreviewOrders.filter(o => o.selected).length === 0}
                style={{
                  padding: '6px 16px', borderRadius: '3px', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                  backgroundColor: (intradayLoading || intradayPreviewOrders.filter(o => o.selected).length === 0) ? '#9ca3af' : '#ef4444', color: 'white',
                }}>
                {intradayLoading ? '처리 중...' : `주문적용 (${intradayPreviewOrders.filter(o => o.selected).length}건)`}
              </button>
            )}
          </div>
          <div className="ag-theme-quartz" style={{ height: Math.min(150 + intradayPreviewOrders.length * 42, 400), width: '100%', border: '2px solid #f59e0b', borderTop: '1px solid #fde68a', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
            <AgGridReact<PreviewOrder> {...copyMenu.gridProps}
              rowData={intradayPreviewOrders}
              columnDefs={[
                {
                  headerName: '', width: 40, pinned: 'left',
                  headerComponent: () => {
                    const allSelected = intradayPreviewOrders.every(o => o.selected);
                    return <input type="checkbox" checked={allSelected} onChange={toggleIntradayPreviewSelectAll} />;
                  },
                  cellRenderer: (p: any) => (
                    <input type="checkbox" checked={p.data.selected ?? false}
                      onChange={() => toggleIntradayPreviewSelect(p.data.prodId)} />
                  ),
                  sortable: false,
                },
                ...previewBaseColDefs,
              ]}
              defaultColDef={{ resizable: true, sortable: true, filter: false }}
              getRowId={p => p.data.prodId}
            />
          </div>
        </div>
      )}

      {intradayError && (
        <div style={{ backgroundColor: '#fee2e2', border: '2px solid #ef4444', borderRadius: '8px', padding: '16px', color: '#991b1b', fontSize: '12px' }}>
          오류: {intradayError}
        </div>
      )}

      {!intradayResult && !intradayLoading && !intradayError && (
        <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
          파라미터를 설정하고 '시뮬레이션 실행' 버튼을 클릭하세요. (분봉 데이터: kwchartmin)
        </div>
      )}

      {/* 실행 트레이스 — 상단 시뮬레이션 파라미터를 controlled 로 공유, "시뮬레이션 실행" 시 자동 호출 */}
      <div style={{ marginTop: '20px' }}>
        <IntradayTraceSection
          showControls={false}
          runTrigger={traceRunTrigger}
          config={{
            prodId: intradayConfig.prodId || '005930',
            fromDate: intradayConfig.fromDate,
            toDate: intradayConfig.toDate,
            barResolution: intradayConfig.barResolution,
            signalFrequency: intradayConfig.tickIntervalMinutes,
          }}
          title={`실행 트레이스 — ${intradayConfig.prodId || '005930'} · ${intradayConfig.barResolution}분봉 / Signal ${intradayConfig.tickIntervalMinutes}분`}
        />
      </div>

      {copyMenu.menu}
    </div>
  );
};

export default QuantIntraday;
